import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import * as _ from 'lodash';

import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import {
  EntityId,
  INewComment,
  IReconcileReason,
  IReconcileTicketDetail,
  IReconcileTicketReasons,
  ReconcileStatus,
  SidebarDetailComponent,
  SidebarDetailService,
  TicketStatus,
} from '@appshared';

import { ReconciledDetailComponent } from '../reconciled-detail-sidebar/reconciled-detail-sidebar.component';
import { ReconciledDetailApiService } from '.';

// this service handle turn on/off the sidebar, integrates with other services, as well as dealing with everything about the detail model
@Injectable({
  providedIn: 'root',
})
export class ReconciledDetailTicketService {
  private viewName = 'reconciledDetailComponent';
  private _confirmDialogRef: MatDialogRef<any>;

  // notify that a ticket has been updated
  ticketUpdatedEvent = new EventEmitter<boolean>();

  constructor(
    private _matDialog: MatDialog,
    private _translate: TranslateService,
    private _sidebarDetailService: SidebarDetailService,
    private _reconcileDetailApiSrv: ReconciledDetailApiService
  ) {}

  public renderView(): void {
    const sidebarDetailComp = new SidebarDetailComponent(ReconciledDetailComponent);
    this._sidebarDetailService.renderComponent(this.viewName, sidebarDetailComp);
  }

  public destroyView(): void {
    // close the view to ensure the white panel will be hidden, then destroy the component
    this._sidebarDetailService.closeView();
    this._sidebarDetailService.destroyComponent(this.viewName);
  }

  public openView(ticketId?: EntityId): void {
    // console.log(`ticket ID is: ${ticketId}`);
    if (ticketId) {
      this._sidebarDetailService.sendDataToView(this.viewName, { reconcileItemId: ticketId });
    }

    this._sidebarDetailService.openView(this.viewName);
  }

  public closeView(): void {
    this._sidebarDetailService.closeView();
  }

  public showSuccesfulDialog(): Observable<any> {
    this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
      disableClose: true,
      autoFocus: false, //Avoid autoFocus when the confirm dialog has the close/cancel button only.
    });
    this._confirmDialogRef.componentInstance.data = {
      svgIcon: 'artstuff_success_response',
      confirmHeader: this._translate.instant('DIALOG.CONFIRM_HEADER'),
      confirmMessage: this._translate.instant('DIALOG.CONFIRM_MESSAGE'),
      cancelBtnText: this._translate.instant('DIALOG.CLOSE_BUTTON_TEXT'),
      isResponse: true,
      hideAcceptBtn: true,
    };
    return this._confirmDialogRef.afterClosed();
  }

  public showStatusDialog(title: string = '', msg: string = '', ticketId: string = ''): void {
    this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, { disableClose: true, autoFocus: false }); //Avoid autoFocus when the confirm dialog has the close/cancel button only.
    this._confirmDialogRef.componentInstance.data = {
      svgIcon: 'artstuff_success_response',
      confirmHeader: this._translate.instant(title),
      confirmMessage: this._translate.instant(msg, { ticketId: ticketId }),
      cancelBtnText: this._translate.instant('DIALOG.CLOSE_BUTTON_TEXT'),
      isResponse: true,
      hideAcceptBtn: true,
    };
  }

  public getListReason(): Observable<IReconcileTicketReasons> {
    return this._reconcileDetailApiSrv.getListReason();
  }

  public showDuplicateOrderModal(): Observable<any> {
    this._confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
      disableClose: true,
    });
    this._confirmDialogRef.componentInstance.data = {
      svgIcon: 'artstuff_inventory',
      confirmHeader: this._translate.instant('DUPLICATE_ORDER_CONFIRMATION.CONFIRM_HEADER'),
      confirmMessage: this._translate.instant('DUPLICATE_ORDER_CONFIRMATION.CONFIRM_MESSAGE'),
      acceptBtnText: this._translate.instant('DUPLICATE_ORDER_CONFIRMATION.ACCEPT_BUTTON_TEXT'),
      cancelBtnText: this._translate.instant('DUPLICATE_ORDER_CONFIRMATION.CLOSE_BUTTON_TEXT'),
      isResponse: false,
      modalWidth: '324px',
      headerClass: 'artstuff-orange-fg',
      hideAcceptBtn: false,
    };
    return this._confirmDialogRef.afterClosed();
  }

  public confirmReason(ticketId: string, confirmFrmValue: any): Observable<any> {
    const observable = new Observable(subscriber => {
      const apiObservable = this._reconcileDetailApiSrv.confirmReason(ticketId, confirmFrmValue).subscribe((res: any) => {
        // if there is res.success from confirm API, notify the need to reload data
        if (res?.success) {
          this.ticketUpdatedEvent.emit(true);
        }

        subscriber.next(res);
      });

      // Provide a way of canceling and disposing the resource
      return function unsubscribe() {
        apiObservable.unsubscribe();
      };
    });

    return observable;
  }

  public updateTicketStatus(ticketId: EntityId, newStatus: TicketStatus): Observable<any> {
    const observable = new Observable(subscriber => {
      const apiObservable = this._reconcileDetailApiSrv.updateTicketStatus(ticketId, newStatus).subscribe((res: any) => {
        // if there is res.success from confirm API, notify the need to reload data
        if (res?.success) {
          this.ticketUpdatedEvent.emit(true);
        }

        subscriber.next(res);
      });

      // Provide a way of canceling and disposing the resource
      return function unsubscribe() {
        apiObservable.unsubscribe();
      };
    });

    return observable;
  }

  public getTicketDetail(ticketId: EntityId): Observable<IReconcileTicketDetail> {
    const observable = new Observable(subscriber => {
      const apiObservable = this._reconcileDetailApiSrv.getTicketDetail(ticketId).subscribe((ticketDetail: IReconcileTicketDetail) => {
        // Normalize the detail for the consistency in FE's display
        if (ticketDetail.paymentInfo?.type === '__') {
          ticketDetail.paymentInfo.type = null;
        }

        // Since BE returns the ascendant list
        // reverse the list to make sure newest comments on the top
        ticketDetail.comments.reverse();

        // Since BE returns the ascendant list
        // reverse the list to make sure newest logs on the top
        ticketDetail.activityLogs.reverse();

        subscriber.next(ticketDetail);
      });

      // Provide a way of canceling and disposing the resource
      return function unsubscribe() {
        apiObservable.unsubscribe();
      };
    });

    return observable;
  }

  public getShippingAddress(shippingAddress): string {
    if (!shippingAddress) {
      return null;
    }
    const { street, country, city, state, postCode } = shippingAddress;
    return `${street || ''}, ${postCode || ''}, ${city || ''}, ${state || ''}, ${country || ''}`;
  }

  public getConfirmationReasons(reasons: IReconcileTicketReasons, reconcileType: ReconcileStatus): IReconcileReason[] {
    // need to get the right reasons from BE responses
    let reconcileStatus = '';
    switch (reconcileType) {
      case ReconcileStatus.ORDER_NOT_FOUND:
        reconcileStatus = 'orderNotFound';
        break;
      case ReconcileStatus.TX_NOT_FOUND:
        reconcileStatus = 'transactionNotFound';
        break;
      case ReconcileStatus.RECONCILED:
      case ReconcileStatus.TO_BE_RECONCILE:
        reconcileStatus = 'toBeReconcile';
        break;
      default:
        break;
    }
    return reasons[reconcileStatus];
  }

  public newComment(ticketId: EntityId, newComment: INewComment) {
    return this._reconcileDetailApiSrv.newComment(ticketId, newComment);
  }
}
